import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, call } from "../api/client";
import { useSystems } from "../api/queries";
import { ConfirmDialog } from "../components/Dialog";
import { ErrorNote } from "../components/ErrorNote";
import { campaignListKey } from "./CampaignListPage";
import { useCampaign, useCampaignQuery, useDraft } from "./context";
import { CampaignError, saveBlob, useAction } from "./parts";
import { InputField, inputDefaults, type InputValues } from "./play";
import type { CampaignRole, PartyMember, WorldView } from "./types";

export function CampaignSettingsPage() {
  const campaign = useCampaign();
  const { head } = campaign;
  if (!campaign.canWrite) return <p className="muted">Die Einstellungen gibt es in dieser Ansicht nicht.</p>;
  // Forms are mounted again when what they edit changes elsewhere, by an undo for example, so they never save an older state.
  return (
    <div className="stack">
      <GeneralSettings key={`${head.name}|${head.system}`} />
      <Members />
      <Party />
      <Terms key={JSON.stringify(head.settings.terms ?? {})} />
      <Calendar key={`${head.system}|${JSON.stringify(head.play.anchor ?? null)}`} />
      <ViaMcp />
      <ExportAndDelete />
    </div>
  );
}

function GeneralSettings() {
  const campaign = useCampaign();
  const { head } = campaign;
  const systems = useSystems();
  const worlds = useQuery({ queryKey: ["worlds"], queryFn: () => call<WorldView[]>(api.GET("/api/worlds")) });
  const [name, setName] = useState(head.name);
  const [system, setSystem] = useState(head.system);
  const action = useAction();
  useDraft(name !== head.name || system !== head.system);
  const free = !systems.data?.some((s) => s.id === system);
  const matching = (worlds.data ?? []).filter((w) => w.system === head.system);
  const suggested = systems.data?.find((s) => !head.systemModule && [s.id, s.name.de, s.name.en].some((n) => n?.localeCompare(head.system, "de", { sensitivity: "base" }) === 0));

  const save = (e: FormEvent) => {
    e.preventDefault();
    const payload: Record<string, unknown> = {};
    if (name !== head.name) payload.name = name;
    if (system.trim() !== head.system) payload.system = system.trim();
    if (Object.keys(payload).length > 0) void action.run(() => campaign.apply({ type: "campaign.settings", payload }));
  };

  return (
    <form className="card stack" onSubmit={save} aria-label="Allgemein">
      <h2>Allgemein</h2>
      <label className="field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field">
        <span>System</span>
        <select value={free ? "" : system} onChange={(e) => setSystem(e.target.value || (free ? system : ""))}>
          {systems.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name.de}
            </option>
          ))}
          <option value="">Anderes System (freie Angabe)</option>
        </select>
      </label>
      {free && (
        <label className="field">
          <span>Name des Systems</span>
          <input value={system} onChange={(e) => setSystem(e.target.value)} />
        </label>
      )}
      {suggested && (
        <p className="notice">
          Für „{head.system}“ gibt es jetzt ein Modul.{" "}
          <button type="button" className="link" onClick={() => setSystem(suggested.id)}>
            {suggested.name.de} zuordnen
          </button>
        </p>
      )}
      {system !== head.system && <p className="muted small">Mit dem System wechselt die Kampagne in die Welt desselben Systems.</p>}
      <div className="actions">
        <button type="submit" className="button primary" disabled={action.busy}>
          Speichern
        </button>
      </div>
      {matching.length > 1 && (
        <label className="field">
          <span>{campaign.term("world")}</span>
          <select
            value={head.world?.id ?? ""}
            onChange={(e) => void action.run(() => campaign.apply({ type: "campaign.settings", payload: { worldId: e.target.value } }))}
          >
            {matching.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {head.world && <WorldName key={`${head.world.id}|${head.world.name}`} world={head.world} />}
      <CampaignError error={action.error} onClose={action.clear} />
    </form>
  );
}

function WorldName({ world }: { world: WorldView }) {
  const campaign = useCampaign();
  const client = useQueryClient();
  const [name, setName] = useState(world.name);
  const action = useAction();
  return (
    <div className="row">
      <label className="field grow">
        <span>{campaign.term("world")}: Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <button
        type="button"
        className="button secondary"
        disabled={name.trim() === world.name || !name.trim()}
        onClick={() =>
          void action.run(async () => {
            await call(api.PATCH("/api/worlds/{id}", { params: { path: { id: world.id } }, body: { name } }));
            await client.invalidateQueries({ queryKey: ["worlds"] });
            campaign.reload();
          })
        }
      >
        Umbenennen
      </button>
      <ErrorNote error={action.error} />
    </div>
  );
}

function useCandidates() {
  const campaign = useCampaign();
  return useCampaignQuery(["candidates"], (headers) => api.GET("/api/campaigns/{id}/candidates", { params: { path: { id: campaign.id } }, headers }));
}

function Members() {
  const campaign = useCampaign();
  const candidates = useCandidates();
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<CampaignRole>("player");
  const action = useAction();
  const roleLabel = (r: CampaignRole) => campaign.term(r === "gameMaster" ? "gameMaster" : "player");
  const others = (candidates.data?.users ?? []).filter((u) => !campaign.head.members.some((m) => m.userId === u.id));
  return (
    <section className="card stack" aria-label="Mitglieder">
      <h2>Mitglieder</h2>
      <ul className="plain-list">
        {campaign.head.members.map((m) => (
          <li key={m.userId} className="row">
            <span className="grow">{m.name}</span>
            <select
              aria-label={`Rolle von ${m.name}`}
              value={m.role ?? "player"}
              onChange={(e) => void action.run(() => campaign.apply({ type: "campaign.member", payload: { userId: m.userId, role: e.target.value } }))}
            >
              <option value="gameMaster">{roleLabel("gameMaster")}</option>
              <option value="player">{roleLabel("player")}</option>
            </select>
            <button type="button" className="link danger" onClick={() => void action.run(() => campaign.apply({ type: "campaign.member", payload: { userId: m.userId } }))}>
              Entfernen
            </button>
          </li>
        ))}
      </ul>
      {others.length > 0 && (
        <form
          className="inline-form two"
          onSubmit={(e) => {
            e.preventDefault();
            if (userId) void action.run(() => campaign.apply({ type: "campaign.member", payload: { userId, role } })).then((ok) => ok && setUserId(""));
          }}
        >
          <label className="field">
            <span>Benutzer</span>
            <select value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">–</option>
              {others.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Rolle</span>
            <select value={role} onChange={(e) => setRole(e.target.value as CampaignRole)}>
              <option value="player">{roleLabel("player")}</option>
              <option value="gameMaster">{roleLabel("gameMaster")}</option>
            </select>
          </label>
          <button type="submit" className="button secondary" disabled={!userId || action.busy}>
            Hinzufügen
          </button>
        </form>
      )}
      <CampaignError error={action.error} onClose={action.clear} />
    </section>
  );
}

function Party() {
  const campaign = useCampaign();
  const [name, setName] = useState("");
  const action = useAction();
  return (
    <section className="card stack" aria-label={campaign.term("party")}>
      <h2>{campaign.term("party")}</h2>
      <ul className="plain-list">
        {campaign.head.party.map((p) => (
          <PartyRow key={`${p.id}|${p.name}|${p.playerName ?? ""}`} member={p} />
        ))}
      </ul>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) void action.run(() => campaign.apply({ type: "campaign.party", payload: { action: "add", name } })).then((ok) => ok && setName(""));
        }}
      >
        <label className="field grow">
          <span>Neu: {campaign.term("member")}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <button type="submit" className="button secondary" disabled={!name.trim() || action.busy}>
          Hinzufügen
        </button>
      </form>
      <CampaignError error={action.error} onClose={action.clear} />
    </section>
  );
}

function PartyRow({ member }: { member: PartyMember }) {
  const campaign = useCampaign();
  const candidates = useCandidates();
  const [name, setName] = useState(member.name);
  const [playerName, setPlayerName] = useState(member.playerName ?? "");
  const action = useAction();
  const update = (payload: Record<string, unknown>) => action.run(() => campaign.apply({ type: "campaign.party", payload: { action: "update", id: member.id, ...payload } }));
  return (
    <li className="stack party-row">
      <div className="inline-form two">
        <label className="field">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} onBlur={() => name.trim() && name !== member.name && void update({ name })} />
        </label>
        <label className="field">
          <span>Charakter</span>
          <select value={member.characterId ?? ""} onChange={(e) => void update({ characterId: e.target.value || null })}>
            <option value="">ohne Charakter</option>
            {candidates.data?.characters.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.ownerName})
              </option>
            ))}
          </select>
          {member.characterId && !member.mine && (
            <span className="muted small">{member.shared ? "Freigegeben zum Lesen und für die Belohnung eines Abschlusses" : `Noch nicht freigegeben; das tut der Besitzer auf der Seite „${campaign.term("party")}“.`}</span>
          )}
        </label>
        <label className="field">
          <span>{campaign.term("player")}</span>
          <select value={member.playerUserId ?? ""} onChange={(e) => void update({ playerUserId: e.target.value || null })}>
            <option value="">–</option>
            {campaign.head.members.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Name ohne Konto</span>
          <input value={playerName} onChange={(e) => setPlayerName(e.target.value)} onBlur={() => playerName !== (member.playerName ?? "") && void update({ playerName })} />
        </label>
      </div>
      <div className="actions">
        <button type="button" className="link danger" onClick={() => void action.run(() => campaign.apply({ type: "campaign.party", payload: { action: "remove", id: member.id } }))}>
          {member.name} entfernen
        </button>
      </div>
      <CampaignError error={action.error} onClose={action.clear} />
    </li>
  );
}

function Terms() {
  const campaign = useCampaign();
  const own = campaign.head.settings.terms ?? {};
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(campaign.head.terms.map((t) => [t.key, own[t.key] ?? ""])));
  const action = useAction();
  useDraft(Object.entries(values).some(([key, value]) => value !== (own[key] ?? "")));
  const save = (e: FormEvent) => {
    e.preventDefault();
    const changed = Object.fromEntries(Object.entries(values).filter(([key, value]) => value.trim() !== (own[key] ?? "")));
    if (Object.keys(changed).length > 0) void action.run(() => campaign.apply({ type: "campaign.settings", payload: { terms: changed } }));
  };
  return (
    <form className="card stack" onSubmit={save} aria-label="Begriffe">
      <h2>Begriffe</h2>
      <p className="muted small">Eigene Bezeichnungen dieser Kampagne; leer bleibt die Vorgabe.</p>
      <div className="terms">
        {campaign.head.terms.map((t) => (
          <label key={t.key} className="field">
            <span>{t.neutral.de}</span>
            <input
              value={values[t.key] ?? ""}
              placeholder={own[t.key] ? t.neutral.de : t.value.de}
              onChange={(e) => setValues((v) => ({ ...v, [t.key]: e.target.value }))}
            />
          </label>
        ))}
      </div>
      <CampaignError error={action.error} onClose={action.clear} />
      <div className="actions">
        <button type="submit" className="button primary" disabled={action.busy}>
          Begriffe speichern
        </button>
      </div>
    </form>
  );
}

/** The anchor of the game calendar: day 1 is this date. With a calendar of the module its inputs, else a free text. */
function Calendar() {
  const campaign = useCampaign();
  const { play } = campaign.head;
  const inputs = play.anchorInputs;
  const stored = play.anchor;
  const [values, setValues] = useState<InputValues>(() =>
    inputs ? { ...inputDefaults(inputs), ...(stored && typeof stored === "object" && !Array.isArray(stored) ? (stored as InputValues) : {}) } : {},
  );
  const [free, setFree] = useState(typeof stored === "string" ? stored : "");
  const action = useAction();
  const [initial] = useState(() => JSON.stringify(values));
  useDraft(inputs ? JSON.stringify(values) !== initial : free !== (typeof stored === "string" ? stored : ""));
  const save = (e: FormEvent) => {
    e.preventDefault();
    void action.run(() => campaign.apply({ type: "campaign.calendar", payload: { anchor: inputs ? values : free } }));
  };
  return (
    <form className="card stack" onSubmit={save} aria-label="Kalender">
      <h2>Kalender</h2>
      <p className="muted small">
        Heute: {play.date.de} ({campaign.term("day")} {play.day}). {campaign.term("day")} 1 ist der Anker.
      </p>
      {inputs ? (
        inputs.map((input) => <InputField key={input.key} input={input} value={values[input.key]} onChange={(v) => setValues((x) => ({ ...x, [input.key]: v }))} />)
      ) : (
        <label className="field">
          <span>{campaign.term("day")} 1 ist (freier Text)</span>
          <input value={free} onChange={(e) => setFree(e.target.value)} />
        </label>
      )}
      {!inputs && <p className="muted small">Das System hat keinen Kalender; gezählt wird „Tag 1“, „Tag 2“ und so weiter.</p>}
      <CampaignError error={action.error} onClose={action.clear} />
      <div className="actions">
        <button type="submit" className="button primary" disabled={action.busy}>
          Anker speichern
        </button>
      </div>
    </form>
  );
}

/** Led by Claude over MCP, the user plays: the interface then shows no scene material (K1). */
function ViaMcp() {
  const campaign = useCampaign();
  const on = !!campaign.head.settings.gameMasterViaMcp;
  const action = useAction();
  return (
    <section className="card stack" aria-label={`${campaign.term("gameMaster")} über MCP`}>
      <h2>{campaign.term("gameMaster")} über MCP</h2>
      <label className="option">
        <input
          type="checkbox"
          checked={on}
          disabled={action.busy}
          onChange={(e) => void action.run(() => campaign.apply({ type: "campaign.settings", payload: { gameMasterViaMcp: e.target.checked } }))}
        />
        {campaign.term("gameMaster")} über MCP
      </label>
      <p className="muted small">
        Leitet Claude die Kampagne über MCP, spielst du selbst mit. Dann zeigt die normale Ansicht kein Szenenmaterial, nur Bekanntes. Das Dossier öffnest du in diesem Fall
        besser nicht; das ist deine Entscheidung, kein technischer Schutz.
      </p>
      <CampaignError error={action.error} onClose={action.clear} />
    </section>
  );
}

function ExportAndDelete() {
  const campaign = useCampaign();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [deleting, setDeleting] = useState(false);
  const action = useAction();
  const exportArchive = () =>
    action.run(async () => {
      const view = campaign.view();
      const response = await campaign.fetch(`/api/campaigns/${campaign.id}/export`);
      if (!response.ok) throw new ApiError(response.status, `Fehler ${response.status}`);
      const disposition = response.headers.get("Content-Disposition") ?? "";
      const name = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1] ?? /filename="?([^";]+)"?/i.exec(disposition)?.[1];
      const blob = await response.blob();
      if (campaign.sameView(view)) saveBlob(blob, name ? decodeURIComponent(name) : "kampagne.zip");
    });
  return (
    <section className="card stack">
      <h2>Sichern und löschen</h2>
      <p className="muted small">
        Das Archiv enthält, was diese Ansicht sieht: {campaign.mode === "dossier" ? "im Dossier alles." : "ohne Dossier nur Bekanntes."}
      </p>
      <div className="actions">
        <button type="button" className="button secondary" disabled={action.busy} onClick={() => void exportArchive()}>
          Als ZIP exportieren
        </button>
        <button type="button" className="button danger" onClick={() => setDeleting(true)}>
          Kampagne löschen
        </button>
      </div>
      <CampaignError error={action.error} onClose={action.clear} />
      {deleting && (
        <ConfirmDialog
          title="Kampagne löschen"
          action="Endgültig löschen"
          danger
          onClose={() => setDeleting(false)}
          onConfirm={async () => {
            await call(api.DELETE("/api/campaigns/{id}", { params: { path: { id: campaign.id } } }));
            await client.invalidateQueries({ queryKey: campaignListKey });
            navigate("/kampagnen");
          }}
        >
          <p>„{campaign.head.name}“ wird mit allen Einträgen gelöscht. Das lässt sich nicht rückgängig machen. Die Fakten der Welt bleiben.</p>
        </ConfirmDialog>
      )}
    </section>
  );
}
